import type { Role, Priority } from '../types';

// ── Brand Colors ──────────────────────────────────────────────────────────────
export const BRAND_COLORS = {
  navy:          '#010E23',
  navyMid:       '#0A1F45',
  navyLight:     '#122560',
  violet:        '#26148C',
  electricViolet:'#5B21FF',
  lavender:      '#A7A1FF',
  cyan:          '#86E9FF',
  magenta:       '#FF31B0',
  white:         '#FFFFFF',
  offWhite:      '#F4F4F8',
  success:       '#1A7A3C',
  successLight:  '#E6F4EC',
  warning:       '#9A6000',
  warningLight:  '#FFF3CD',
  danger:        '#A32D2D',
  dangerLight:   '#FCEBEB',
  gray:          '#5F5E5A',
  grayLight:     '#F1EFE8',
  border:        'rgba(0,0,0,0.10)',
} as const;

// ── Service Categories — match prototype & backend sanitizeItem ───────────────
export const SERVICE_CATEGORIES = [
  'Access Management',
  'Email & Account Services',
  'Software & Application',
  'Hardware & Equipment',
  'Server & Infrastructure',
  'Network & Connectivity',
  'New User Onboarding',
  'General / Other',
  'AI Tool / Application Request',
  'Cloud Services Access',
  'Access Request',
  'Infrastructure / Server Change',
  'Third-Party Vendor Onboarding',
  'Data Export / Sharing Request',
] as const;

export type ServiceCategory = typeof SERVICE_CATEGORIES[number];

export const SERVICE_CATEGORY_META: Array<{ id: string; label: ServiceCategory; icon: string }> = [
  { id: 'access',     label: 'Access Management',       icon: '🔐' },
  { id: 'email',      label: 'Email & Account Services', icon: '📧' },
  { id: 'software',   label: 'Software & Application',  icon: '💻' },
  { id: 'hardware',   label: 'Hardware & Equipment',     icon: '🖥️' },
  { id: 'server',     label: 'Server & Infrastructure', icon: '⚙️' },
  { id: 'network',    label: 'Network & Connectivity',  icon: '🌐' },
  { id: 'onboarding', label: 'New User Onboarding',     icon: '👤' },
  { id: 'other',      label: 'General / Other',         icon: '🔧' },
  { id: 'ai-tool',    label: 'AI Tool / Application Request', icon: '🤖' },
  { id: 'cloud',      label: 'Cloud Services Access',   icon: '☁️' },
  { id: 'access-req', label: 'Access Request',          icon: '🔑' },
  { id: 'infra',      label: 'Infrastructure / Server Change', icon: '🏗️' },
  { id: 'vendor',     label: 'Third-Party Vendor Onboarding', icon: '🤝' },
  { id: 'data-export',label: 'Data Export / Sharing Request', icon: '📤' },
];

export const PRIORITIES: Priority[]     = ['Low', 'Medium', 'High', 'Critical'];
export const SLA_HOURS: Record<Priority, number> = { Low: 72, Medium: 24, High: 8, Critical: 4 };

export const ROLES: Role[]              = ['employee', 'line_manager', 'it_manager', 'it_staff', 'top_management', 'admin'];
export const ROLE_LABELS: Record<Role, string> = {
  employee:       'Employee',
  line_manager:   'Line Manager',
  it_manager:     'IT Manager',
  it_staff:       'IT Staff',
  top_management: 'Top Management',
  admin:          'Admin',
};

// Departments are no longer hardcoded — they come live from GET /api/departments
// via the useDepartments() hook so newly-added departments appear without a reload.

// Status display labels — Prisma enum JS values → human readable
export const STATUS_LABELS: Record<string, string> = {
  Draft:                'Draft',
  PendingLineManager:   'Pending LM Approval',
  PendingITManager:     'Pending IT Manager',
  PendingTopManagement: 'Pending Top Management',
  PendingRequester:     'Clarification Required',
  Approved:             'Approved',
  Assigned:             'Assigned',
  InProgress:           'In Progress',
  Deferred:             'Deferred',
  Resolved:             'Resolved — Awaiting Confirmation',
  Completed:            'Completed',
  ClosedCompleted:      'Closed – Resolved',
  ClosedCannotImplement:'Closed – Not Resolved',
  ClosedOutOfScope:     'Closed – Out of Scope',
  ClosedDuplicate:      'Closed – Duplicate',
  Rejected:             'Rejected',
  Closed:               'SR Closed',
};

// Covers both Prisma enum JS values (what the API returns) and legacy display-string keys
export const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  // Active lifecycle
  Draft:                { color: '#5F5E5A', bg: '#F1EFE8' },
  PendingLineManager:   { color: '#9A6000', bg: '#FFF3CD' },  // amber
  PendingITManager:     { color: '#c2410c', bg: '#fff7ed' },  // orange
  PendingTopManagement: { color: '#7c3aed', bg: '#f5f3ff' },  // purple
  PendingRequester:     { color: '#6d28d9', bg: '#ede9fe' },
  Approved:             { color: '#1A7A3C', bg: '#E6F4EC' },
  Assigned:             { color: '#185FA5', bg: '#E6F1FB' },  // blue
  InProgress:           { color: '#3730a3', bg: '#e0e7ff' },  // indigo
  Deferred:             { color: '#4b5563', bg: '#e5e7eb' },
  Resolved:             { color: '#0f766e', bg: '#ccfbf1' },
  // Closed
  Completed:            { color: '#1A7A3C', bg: '#E6F4EC' },
  ClosedCompleted:      { color: '#15803d', bg: '#dcfce7' },  // green – resolved
  ClosedCannotImplement:{ color: '#b91c1c', bg: '#fee2e2' },  // red – not resolved
  ClosedOutOfScope:     { color: '#A32D2D', bg: '#FCEBEB' },
  ClosedDuplicate:      { color: '#A32D2D', bg: '#FCEBEB' },
  Rejected:             { color: '#b91c1c', bg: '#fee2e2' },  // red
  Closed:               { color: '#374151', bg: '#F3F4F6' },
  // Legacy display-string keys (backward compat)
  'Pending LM Approval':  { color: '#9A6000', bg: '#FFF3CD' },
  'Pending IT Approval':  { color: '#c2410c', bg: '#fff7ed' },
  'In Progress':          { color: '#3730a3', bg: '#e0e7ff' },
  'Pending Line Manager': { color: '#9A6000', bg: '#FFF3CD' },
  'Pending IT Manager':   { color: '#c2410c', bg: '#fff7ed' },
  'Pending Requester':    { color: '#6d28d9', bg: '#ede9fe' },
  'Closed Completed':     { color: '#15803d', bg: '#dcfce7' },
  'Closed Cannot Implement': { color: '#b91c1c', bg: '#fee2e2' },
  'Closed Out Of Scope':  { color: '#A32D2D', bg: '#FCEBEB' },
  'Closed Duplicate':     { color: '#A32D2D', bg: '#FCEBEB' },
};

export const PRIORITY_COLORS: Record<string, { color: string; bg: string }> = {
  Critical: { color: '#A32D2D', bg: '#FCEBEB' },
  High:     { color: '#9A6000', bg: '#FFF3CD' },
  Medium:   { color: '#185FA5', bg: '#E6F1FB' },
  Low:      { color: '#5F5E5A', bg: '#F1EFE8' },
};

export const TASK_CATEGORIES = [
  'Ticket Resolution', 'Server Maintenance', 'Network Configuration',
  'User Support', 'Software Deployment', 'Hardware Installation',
  'Security & Compliance', 'Documentation', 'Training', 'Project Work',
  'Meeting', 'Other',
] as const;

export const REF_NUMBER_PREFIX = 'PSH-IT-FRM';
export const REF_NUMBER_START  = 21;
